/**
 * Profile logic needed for both the learning dashboard and other profile tabs.
 * Responsible for initializing model state, creating the profile and
 * rendering the nav, content chrome, and inner tab chrome.
 */

import _ from "underscore";

import {redirect} from "@repo/urls";

import $ from "#jquery/jquery.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
import ProfileModel, {
    type ProfileModelType,
} from "#shared-package/profile-model.ts";

import ProfileContentChrome from "./profile-content-chrome.tsx";

export type ViewPermissions = {
    // Whether the current user can modify the coach/parent list
    isCoachListWritable: boolean;
    // Whether the person viewing the profile is a managing coach of the user
    // being viewed
    isManagedByActor: boolean;
};

type Options = Record<string, any>;

export type Profile = {
    profile: ProfileModelType | null;
    init: (options: Options) => void;
    viewPermissions: ViewPermissions;
    profileRoot: string | null | undefined;
    spinnerTimer: number | null | undefined;
    spinnerTimeout: number;
    spinnerInitialized: boolean;
    renderContentChrome: () => void;
    initSpinner: () => void;
    showSpinner: () => void;
    hideSpinner: () => void;
    isMetadash: (route: string) => boolean;
    isProfileSheet: (route: string, options?: Options) => boolean;
    showNotification: (className: string) => void;
    getBaseRequestParams: (profile?: ProfileModelType | null) => Params;
    shouldShowProfileHeader: (route: string, options?: Options) => boolean;
};

type Params = {
    casing: string;
    kaid?: string;
    username?: string;
    banned?: 1 | 0;
    hellban_reason?: string;
};

type InitOptions = {
    profile: ProfileModelType | null;
    profileData: Record<string, any>;
    viewPermissions: ViewPermissions | null | undefined;
};

const ProfileSingleton: Profile = {
    profile: null,
    router: null,
    userCardView: null,

    viewPermissions: {
        isCoachListWritable: false,
        isManagedByActor: false,
    },

    /**
     * The root segment of the URL for the profile page for this user.
     * Will be of the form "/profile/<identifier>" where identifier
     * can be a username, or other identifier sent by the server.
     */
    profileRoot: "",

    /**
     * We wait a certain amount of time before actually showing the spinner
     * to avoid a "sparkle" effect.
     */
    spinnerTimer: null,
    spinnerTimeout: 200,
    spinnerInitialized: false,

    /**
     * Handle a change to the profile root.
     */
    _onProfileUpdated: function (this: any) {
        const username = this.profile.get("username");
        if (
            username &&
            ProfileSingleton.profileRoot !== "/profile/" + username + "/"
        ) {
            // Profile root changed - we need to reload the page since
            // Backbone.router isn't happy when the root changes.
            redirect("/profile/" + username + "/");
        }
    },

    /**
     * Called to initialize the profile page.
     *
     * See learning-dashboard.jsx for details.
     */
    // @ts-expect-error - TS2322 - Type '(this: any, options: InitOptions) => void' is not assignable to type '(options: Options) => void'.
    init: function (this: any, options: InitOptions) {
        if (options.profile) {
            this.profile = options.profile;
        } else {
            this.profile = new ProfileModel(options.profileData);
        }

        if (!this.profile) {
            return;
        }

        this.profile.bind("savesuccess", this._onProfileUpdated, this);

        const root = this.profile.get("profileRoot");

        this.profileRoot = root;

        if (options.viewPermissions) {
            this.viewPermissions = options.viewPermissions;
        } else {
            this.viewPermissions = {
                isCoachListWritable: false,
                isManagedByActor: false,
            };
        }

        ProfileSingleton.renderContentChrome();
    },

    /**
     * Render the chrome for main content area on the right, including the
     * containers for the learning dashboard and spinner.
     */
    renderContentChrome: function () {
        const section = document.createElement("section");
        section.classList.add("ka-tab-content");
        $("#dashboard-profile-content").append(section);
        deprecatedReactRender(<ProfileContentChrome />, section);
    },

    /**
     * Show the progress animation.
     */
    showSpinner: function (this: any) {
        if (ProfileSingleton.spinnerTimer) {
            return;
        }

        ProfileSingleton.initSpinner();
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
        // eslint-disable-next-line no-restricted-syntax
        this.spinnerTimer = setTimeout(function () {
            $("#profile-spinner").fadeIn("fast");
        }, ProfileSingleton.spinnerTimeout);
    },

    initSpinner: _.once(function () {
        if (ProfileSingleton.spinnerInitialized) {
            return;
        }
        ProfileSingleton.spinnerInitialized = true;
    }),

    /**
     * Hide the progress animation.
     */
    hideSpinner: function () {
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
        // @ts-expect-error - TS2769 - No overload matches this call.
        // eslint-disable-next-line no-restricted-syntax
        clearTimeout(ProfileSingleton.spinnerTimer);
        delete ProfileSingleton.spinnerTimer;
        $("#profile-spinner").hide();
    },

    /**
     * Show a profile notification
     * Expects the class name of the div to show, such as "error-graph"
     */
    showNotification: function (className: string) {
        ProfileSingleton.hideSpinner();

        const jel = $(".profile-notification")
            .removeClass("uncover-nav")
            .removeClass("cover-top");

        if (className === "empty-graph") {
            jel.addClass("uncover-nav");
        }

        jel.show()
            .find("." + className)
            .show()
            .siblings()
            .hide();
    },

    isProfileSheet: function (route: string, options?: Options) {
        if (options) {
            return options.onProfileSheet;
        }

        // TODO (jessie): Currently all of our profile pages begin with
        // profile/ but that could change
        return route.indexOf("/profile") === 0;
    },

    isMetadash: function (route: string) {
        return !route || route === "/";
    },

    shouldShowProfileHeader: function (route: string, options?: Options) {
        return (
            ProfileSingleton.isMetadash(route) ||
            ProfileSingleton.isProfileSheet(route, options)
        );
    },

    /**
     * Return an object to be used in an outgoing XHR for user profile data
     * (e.g. activity graph data).
     * This includes an identifier for the current profile being viewed at,
     * and other common properties.
     */
    getBaseRequestParams: function (profile?: ProfileModelType | null): Params {
        if (!profile) {
            profile = ProfileSingleton.profile;
        }

        // TODO(alan): Get rid of casing=camel here once all uses have moved to
        // use signatures.
        const params: Params = {
            casing: "camel",
        };
        if (profile) {
            // Allow the server to determine which identifier is best to
            // use for their lookup in each case.

            if (profile.get("kaid")) {
                params.kaid = profile.get("kaid");
            }

            if (profile.get("username")) {
                params.username = profile.get("username");
            }
        }
        return params;
    },
};

export default ProfileSingleton;
